import { Shape, ShapeType } from './types';
import { normalizeShapeZIndexes } from '../utils/helpers/shapeZIndex';

/**
 * Wire-format version of the persisted `shapesConfig` blob. Bump when the
 * serialized shape contract changes in a non-backwards-compatible way and
 * add a corresponding branch to `migrateShapesConfig`.
 */
export const CURRENT_CONFIG_VERSION = 1;

interface ShapesConfigEnvelope {
  version: number;
  shapes: unknown[];
}

const isEnvelope = (value: unknown): value is ShapesConfigEnvelope =>
  typeof value === 'object' &&
  value !== null &&
  'version' in value &&
  'shapes' in value &&
  Array.isArray((value as { shapes: unknown }).shapes);

const isString = (v: unknown): v is string => typeof v === 'string';
const isNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isNumberArray = (v: unknown): v is number[] => Array.isArray(v) && v.every(isNumber);

const hasBaseShapeFields = (v: Record<string, unknown>): boolean => isString(v.id) && isString(v.floorId) && isNumber(v.x) && isNumber(v.y);
const hasValidOptionalBaseFields = (v: Record<string, unknown>): boolean => v.zIndex === undefined || isNumber(v.zIndex);
const hasValidOptionalLabelFields = (v: Record<string, unknown>): boolean =>
  (v.fontSize === undefined || (isNumber(v.fontSize) && v.fontSize > 0)) && (v.labelRotation === undefined || isNumber(v.labelRotation));

const validateShape = (candidate: unknown): Shape | null => {
  if (!candidate || typeof candidate !== 'object') return null;
  const v = candidate as Record<string, unknown>;
  if (!hasBaseShapeFields(v)) return null;
  if (!hasValidOptionalBaseFields(v)) return null;

  switch (v.type) {
    case ShapeType.RECTANGLE:
      if (
        isNumber(v.width) &&
        isNumber(v.height) &&
        isString(v.fill) &&
        isString(v.stroke) &&
        isNumber(v.strokeWidth) &&
        isString(v.label) &&
        hasValidOptionalLabelFields(v)
      ) {
        return candidate as Shape;
      }
      return null;
    case ShapeType.CIRCLE:
      if (
        isNumber(v.radiusX) &&
        isNumber(v.radiusY) &&
        isString(v.fill) &&
        isString(v.stroke) &&
        isNumber(v.strokeWidth) &&
        isString(v.label) &&
        hasValidOptionalLabelFields(v)
      ) {
        return candidate as Shape;
      }
      return null;
    case ShapeType.LINE:
      if (isNumberArray(v.points) && isString(v.stroke) && isNumber(v.strokeWidth)) {
        return candidate as Shape;
      }
      return null;
    case ShapeType.TEXT:
      if (isString(v.label) && hasValidOptionalLabelFields(v)) return candidate as Shape;
      return null;
    case ShapeType.POLYGON:
      if (isNumberArray(v.points) && isString(v.stroke) && isNumber(v.strokeWidth)) {
        return candidate as Shape;
      }
      return null;
    case ShapeType.POLYGON_BOX:
      if (
        isNumberArray(v.points) &&
        isString(v.fill) &&
        isString(v.stroke) &&
        isNumber(v.strokeWidth) &&
        isString(v.label) &&
        hasValidOptionalLabelFields(v)
      ) {
        return candidate as Shape;
      }
      return null;
    default:
      return null;
  }
};

/**
 * Migration hook reserved for future schema bumps. Today the only valid
 * version is 1, so this is a passthrough.
 */
const migrateShapesConfig = (envelope: ShapesConfigEnvelope): ShapesConfigEnvelope => {
  if (envelope.version === CURRENT_CONFIG_VERSION) return envelope;
  return envelope;
};

export const serializeShapes = (shapes: Shape[]): string =>
  JSON.stringify({ version: CURRENT_CONFIG_VERSION, shapes } satisfies ShapesConfigEnvelope);

/**
 * Tolerant inverse of `serializeShapes`. Accepts:
 *   - the current envelope `{ version, shapes }`
 *   - a legacy bare array (pre-versioning)
 *   - `null` / `undefined` / empty string (returns `[]`)
 *
 * Invalid JSON or individual shapes that fail `validateShape` are dropped
 * with a warning so a single corrupted record can't break the whole floor.
 */
export const deserializeShapes = (raw: string | null | undefined): Shape[] => {
  if (!raw) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[WarehouseMap] shapesConfig JSON invalid, dropping', e);
    return [];
  }

  let candidates: unknown[];
  if (isEnvelope(parsed)) {
    candidates = migrateShapesConfig(parsed).shapes;
  } else if (Array.isArray(parsed)) {
    candidates = parsed;
  } else {
    candidates = [];
  }

  const valid: Shape[] = [];
  for (const candidate of candidates) {
    const shape = validateShape(candidate);
    if (shape) {
      valid.push(shape);
    } else {
      // eslint-disable-next-line no-console
      console.warn('[WarehouseMap] dropping invalid shape', candidate);
    }
  }
  return normalizeShapeZIndexes(valid);
};
